import { AlertCircle, CheckCircle2, ClipboardList, Coins, Flag, Loader2, X, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useFarmInbox } from '../hooks/useFarmOrder';
import { PageShell } from '@/components/layout/PageShell';
import { EmptyState } from '@/components/ui/EmptyState';
import { CurrencyInput } from '@/components/ui/CurrencyInput';
import { formatBRL, formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { OrderStatus } from '../types';

const STATUS_LABELS: Record<OrderStatus, string> = {
  quote:     'Aguardando orçamento',
  reserved:  'Reservado',
  printing:  'Em impressão',
  done:      'Concluído',
  cancelled: 'Cancelado',
  refunded:  'Reembolsado',
};

const STATUS_CHIPS: Record<OrderStatus, string> = {
  quote:     'chip-neutral',
  reserved:  'chip-info',
  printing:  'chip-warning',
  done:      'chip-positive',
  cancelled: 'chip-negative',
  refunded:  'chip-neutral',
};

const FILTERS: { label: string; value: OrderStatus | 'all' }[] = [
  { label: 'Todos',      value: 'all' },
  { label: 'Cotações',   value: 'quote' },
  { label: 'Reservados', value: 'reserved' },
  { label: 'Imprimindo', value: 'printing' },
  { label: 'Concluídos', value: 'done' },
  { label: 'Cancelados', value: 'cancelled' },
];

import { useState } from 'react';

export function FarmInboxPage() {
  const { orders, loading, error, changeStatus } = useFarmInbox();
  const [filter, setFilter]   = useState<OrderStatus | 'all'>('all');
  const [busy, setBusy]       = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Orçamento de "atestado de leigo": dono define o preço
  const [quoting, setQuoting] = useState<string | null>(null);
  const [quoteVal, setQuoteVal] = useState(0);
  const [quoteBusy, setQuoteBusy] = useState(false);
  // Denúncia de pedido
  const [reportId, setReportId] = useState<{ id: string; farmId: string } | null>(null);
  const [reportReason, setReportReason] = useState('');
  const [reportBusy, setReportBusy] = useState(false);
  const [reportMsg, setReportMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const visible = filter === 'all' ? orders : orders.filter((o) => o.status === filter);
  const quoteCount = orders.filter((o) => o.status === 'quote').length;

  async function handle(id: string, status: OrderStatus) {
    setBusy(id);
    setActionError(null);
    try {
      await changeStatus(id, status);
    } catch (e: any) {
      setActionError(e.message ?? 'Erro ao atualizar pedido.');
    } finally {
      setBusy(null);
    }
  }

  // ─── Aceite com prazo e forma de entrega ──────────────────────────────────
  const [accepting, setAccepting]       = useState<(typeof orders)[number] | null>(null);
  const [acceptDays, setAcceptDays]     = useState(7);
  const [acceptMethod, setAcceptMethod] = useState<'retirada' | 'entrega_local' | 'correios'>('retirada');
  const [acceptBusy, setAcceptBusy]     = useState(false);

  async function confirmAccept() {
    if (!accepting) return;
    setAcceptBusy(true);
    setActionError(null);
    try {
      const newSpecs = {
        ...(accepting.specs ?? {}),
        supplier_deadline_days: acceptDays,
        supplier_delivery_method: acceptMethod,
      };
      const { error: err } = await supabase
        .from('farm_orders')
        .update({ status: 'reserved', specs: newSpecs } as never)
        .eq('id', accepting.id);
      if (err) throw new Error(err.message);
      setAccepting(null);
      window.location.reload();
    } catch (e: any) {
      setActionError(e.message ?? 'Erro ao aceitar a cotação.');
      setAcceptBusy(false);
    }
  }

  async function sendQuote(id: string) {
    if (quoteVal <= 0) { setActionError('Informe um valor de orçamento válido.'); return; }
    setQuoteBusy(true);
    setActionError(null);
    try {
      const fee = Math.round(quoteVal * 0.07);
      const { error: err } = await supabase
        .from('farm_orders')
        .update({
          supplier_amount_cents: quoteVal,
          platform_fee_cents:    fee,
          total_amount_cents:    quoteVal + fee,
          status:                'reserved',
        } as never)
        .eq('id', id);
      if (err) throw new Error(err.message);
      window.location.reload();
    } catch (e: any) {
      setActionError(e.message ?? 'Erro ao enviar orçamento.');
      setQuoteBusy(false);
    }
  }

  async function sendReport() {
    if (!reportId || reportReason.trim().length < 10) return;
    setReportBusy(true);
    try {
      const { data: resp, error: err } = await supabase.functions.invoke('report-farm', {
        body: { farm_id: reportId.farmId, order_id: reportId.id, reason: reportReason.trim() },
      });
      if (err) throw new Error(err.message);
      if (!resp?.ok) throw new Error(resp?.error ?? 'Falha ao enviar denúncia.');
      setReportMsg({
        ok: true,
        text: `Denúncia registrada (protocolo ${String(resp.protocol).slice(0, 8)}). ${resp.email_sent ? 'O suporte foi notificado por e-mail.' : 'O suporte fará a análise em breve.'}`,
      });
      setReportReason('');
    } catch (e: any) {
      setReportMsg({ ok: false, text: e.message ?? 'Erro ao enviar denúncia.' });
    } finally {
      setReportBusy(false);
    }
  }

  return (
    <PageShell
      title="Pedidos recebidos"
      subtitle="Gerencie os pedidos feitos nas suas farms."
      back="/farms"
    >
      {/* Filtros */}
      <div className="flex gap-2 flex-wrap mb-4">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={cn(
              'px-3 py-1.5 rounded-xl text-xs font-medium transition-colors relative',
              filter === f.value
                ? 'bg-[#14a655] text-white'
                : 'bg-surface-subtle text-ink-muted hover:text-ink'
            )}
          >
            {f.label}
            {f.value === 'quote' && quoteCount > 0 && (
              <span className="ml-1.5 bg-white/30 text-white rounded-full px-1.5 py-0.5 text-[10px] font-bold">
                {quoteCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {actionError && (
        <div className="flex items-center justify-between gap-2 mb-4 rounded-lg bg-negative/10 border border-negative/20 px-3 py-2 text-sm text-negative">
          <div className="flex items-center gap-2"><AlertCircle size={16} className="shrink-0" />{actionError}</div>
          <button onClick={() => setActionError(null)} className="shrink-0"><X size={14} /></button>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-24 rounded-2xl bg-surface-subtle animate-pulse" />)}
        </div>
      ) : error ? (
        <div className="card p-6 flex items-start gap-3 text-negative">
          <AlertCircle size={18} className="shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-sm">Erro ao carregar pedidos</p>
            <p className="text-xs mt-1 text-ink-muted">{error}</p>
          </div>
        </div>
      ) : visible.length === 0 ? (
        <div className="card p-8">
          <EmptyState
            icon={ClipboardList}
            title={filter === 'all' ? 'Nenhum pedido recebido' : 'Nenhum pedido neste filtro'}
            description={filter === 'all'
              ? 'Quando clientes solicitarem cotações nas suas farms, elas aparecerão aqui.'
              : 'Tente outro filtro para ver mais pedidos.'}
          />
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((o) => (
            <div key={o.id} className="card p-5">
              <div className="flex items-start gap-3">
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={STATUS_CHIPS[o.status]}>{STATUS_LABELS[o.status]}</span>
                    <span className="text-xs text-ink-soft font-mono">{o.id.slice(0, 8)}…</span>
                  </div>
                  <p className="text-xs text-ink-muted mt-1">
                    <span className="font-medium text-ink">{o.farmName}</span>
                    {' · '}
                    {formatDate(o.created_at)}
                  </p>
                  <p className="text-sm font-semibold text-ink mt-1 tabular-nums">
                    {formatBRL(o.total_amount_cents)}
                    <span className="text-xs font-normal text-ink-muted ml-1">
                      (fornecedor: {formatBRL(o.supplier_amount_cents)})
                    </span>
                  </p>
                  {o.specs && (
                    <p className="text-xs text-ink-muted mt-1">
                      {[
                        o.specs.quantity != null ? `${String(o.specs.quantity)} peça(s)` : null,
                        o.specs.hours_estimated != null ? `${String(o.specs.hours_estimated)}h de máquina` : null,
                        o.specs.material != null ? String(o.specs.material) : null,
                        o.specs.colors != null ? `cores: ${String(o.specs.colors)}` : null,
                        o.specs.filament_grams != null ? `${String(o.specs.filament_grams)}g de filamento` : null,
                        o.specs.machine_brand != null || o.specs.machine_model != null
                          ? [o.specs.machine_brand, o.specs.machine_model].filter(Boolean).map(String).join(' ')
                          : null,
                        o.specs.delivery_km != null ? `entrega ${String(o.specs.delivery_km)} km` : null,
                        o.specs.project_file_url != null ? 'com arquivo do projeto' : null,
                        o.specs.modeling_requested === true ? '⭐ modelagem 3D solicitada' : null,
                        o.specs.atestado_leigo === true ? '📝 orçamento a montar (atestado de leigo)' : null,
                      ].filter(Boolean).join(' · ')}
                    </p>
                  )}
                </div>

                {/* Link detalhe + denúncia */}
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Link to={`/farms/pedidos/${o.id}`} className="text-xs text-brand-700 underline">
                    Ver detalhe
                  </Link>
                  {o.status !== 'cancelled' && o.status !== 'done' && o.status !== 'refunded' && (
                    <button
                      onClick={() => { setReportId({ id: o.id, farmId: o.farm_id }); setReportReason(''); setReportMsg(null); }}
                      className="text-[11px] text-ink-muted hover:text-negative flex items-center gap-1"
                      title="Denunciar este pedido ao suporte"
                    >
                      <Flag className="w-3 h-3" /> Denunciar
                    </button>
                  )}
                </div>
              </div>

              {/* Ações para cotações pendentes — atestado de leigo: dono monta o preço */}
              {o.status === 'quote' && o.specs?.atestado_leigo === true && (
                quoting === o.id ? (
                  <div className="mt-4 pt-4 border-t border-border space-y-2">
                    <label className="block text-xs text-ink-muted">Valor do orçamento (à farm, sem a taxa da plataforma)</label>
                    <CurrencyInput className="input w-full" value={quoteVal} onChange={setQuoteVal} />
                    <p className="text-[11px] text-ink-soft">
                      A taxa da plataforma (7%) é somada automaticamente. O cliente aprova pagando.
                    </p>
                    <div className="flex gap-2">
                      <button onClick={() => void sendQuote(o.id)} disabled={quoteBusy}
                        className="btn-primary !py-1.5 !text-xs gap-1.5 flex-1">
                        {quoteBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Coins className="w-3.5 h-3.5" />}
                        {quoteBusy ? 'Enviando…' : 'Enviar orçamento ao cliente'}
                      </button>
                      <button onClick={() => setQuoting(null)} disabled={quoteBusy} className="btn-secondary !py-1.5 !text-xs">
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                    <button
                      onClick={() => { setQuoting(o.id); setQuoteVal(o.supplier_amount_cents || 0); setActionError(null); }}
                      className="btn-primary !py-1.5 !text-xs gap-1.5 flex-1"
                    >
                      <Coins className="w-3.5 h-3.5" />
                      Montar orçamento
                    </button>
                    <button
                      onClick={() => void handle(o.id, 'cancelled')}
                      disabled={busy === o.id}
                      className="btn-secondary !py-1.5 !text-xs gap-1.5 text-negative border-negative/30"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      Recusar
                    </button>
                  </div>
                )
              )}

              {/* Ações para cotações pendentes — cotação normal */}
              {o.status === 'quote' && o.specs?.atestado_leigo !== true && (
                <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                  <button
                    onClick={() => { setAccepting(o); setAcceptDays(7); setAcceptMethod('retirada'); }}
                    disabled={busy === o.id}
                    className="btn-primary !py-1.5 !text-xs gap-1.5 flex-1"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {busy === o.id ? 'Processando…' : 'Aceitar cotação'}
                  </button>
                  <button
                    onClick={() => void handle(o.id, 'cancelled')}
                    disabled={busy === o.id}
                    className="btn-secondary !py-1.5 !text-xs gap-1.5 text-negative border-negative/30"
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    Recusar
                  </button>
                </div>
              )}

              {/* Ações para pedidos reservados */}
              {o.status === 'reserved' && (
                <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                  <button
                    onClick={() => void handle(o.id, 'printing')}
                    disabled={busy === o.id}
                    className="btn-primary !py-1.5 !text-xs gap-1.5"
                  >
                    {busy === o.id ? 'Processando…' : '▶ Iniciar impressão'}
                  </button>
                  <button
                    onClick={() => void handle(o.id, 'cancelled')}
                    disabled={busy === o.id}
                    className="btn-secondary !py-1.5 !text-xs text-negative border-negative/30"
                  >
                    Cancelar
                  </button>
                </div>
              )}

              {/* Ações para impressão em andamento */}
              {o.status === 'printing' && (
                <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                  <button
                    onClick={() => void handle(o.id, 'done')}
                    disabled={busy === o.id}
                    className="btn-primary !py-1.5 !text-xs gap-1.5"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {busy === o.id ? 'Processando…' : 'Marcar como concluído'}
                  </button>
                  <button
                    onClick={() => void handle(o.id, 'cancelled')}
                    disabled={busy === o.id}
                    className="btn-secondary !py-1.5 !text-xs text-negative border-negative/30"
                  >
                    Cancelar pedido
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal de denúncia de pedido */}
      {reportId && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !reportBusy && setReportId(null)}>
          <div className="card w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg text-ink flex items-center gap-2">
                <Flag className="w-4 h-4 text-negative" />
                Denunciar pedido
              </h3>
              <button onClick={() => setReportId(null)} disabled={reportBusy} className="text-ink-muted hover:text-ink">
                <X className="w-5 h-5" />
              </button>
            </div>
            {reportMsg ? (
              <>
                <div className={cn('rounded-xl px-4 py-3 text-sm', reportMsg.ok ? 'bg-positive/10 text-positive border border-positive/20' : 'bg-negative/10 text-negative border border-negative/20')}>
                  {reportMsg.text}
                </div>
                <button onClick={() => setReportId(null)} className="btn-secondary w-full">Fechar</button>
              </>
            ) : (
              <>
                <p className="text-sm text-ink-muted">
                  Descreva o problema com este pedido. A denúncia será registrada e enviada ao suporte
                  para análise. A descrição é obrigatória (mínimo 10 caracteres).
                </p>
                <textarea
                  className="input w-full"
                  rows={5}
                  maxLength={2000}
                  placeholder="Descreva o que aconteceu com o máximo de detalhes…"
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => void sendReport()}
                    disabled={reportBusy || reportReason.trim().length < 10}
                    className="btn-primary flex-1 gap-2"
                  >
                    {reportBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                    {reportBusy ? 'Enviando…' : 'Enviar denúncia'}
                  </button>
                  <button onClick={() => setReportId(null)} disabled={reportBusy} className="btn-secondary">Cancelar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal de aceite: prazo e forma de entrega/retirada */}
      {accepting && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !acceptBusy && setAccepting(null)}>
          <div className="card w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-lg text-ink flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-positive" />
              Aceitar cotação
            </h3>
            <p className="text-sm text-ink-muted">
              Informe ao cliente o prazo estimado e como o pedido será entregue ou retirado.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-ink-muted mb-1">Prazo de entrega (dias) *</label>
                <input type="number" min={1} max={90} className="input w-full" value={acceptDays}
                  onChange={(e) => setAcceptDays(Math.max(1, Number(e.target.value)))} />
              </div>
              <div>
                <label className="block text-xs text-ink-muted mb-1">Forma de entrega *</label>
                <select className="input w-full" value={acceptMethod}
                  onChange={(e) => setAcceptMethod(e.target.value as typeof acceptMethod)}>
                  <option value="retirada">Retirada no local</option>
                  <option value="entrega_local">Entrega local</option>
                  <option value="correios">Envio (Correios/transportadora)</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => void confirmAccept()} disabled={acceptBusy} className="btn-primary flex-1 gap-2">
                {acceptBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                {acceptBusy ? 'Confirmando…' : 'Confirmar aceite'}
              </button>
              <button onClick={() => setAccepting(null)} disabled={acceptBusy} className="btn-secondary">Voltar</button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
